import Component from '@glimmer/component';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import ChecklistRow from './checklist-row';
import { isStepDone, summarizeRemaining } from 'tio-employee/utils/tas/evidence-checklist';
import { EVIDENCE_UPLOAD_ONLY_STATES } from 'tio-employee/utils/tas/attention-tasks';
import Button from 'tio-ui/components/button';
import ErrorMessages from 'tio-ui/components/error-messages';
import { PhUploadSimple } from 'ember-phosphor-icons';
import AcceptConditions from 'tio-employee/components/tuition-assistance/forms/accept-conditions';
import type { SubmitEvidenceBundle } from './submit-evidence';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';

interface SubmitEvidenceActionsSignature {
  Args: {
    application: TasApplicationModel;
    /** Optional so isolated tests can render without the ManagePage-owned
     * bundle; every real render passes one. */
    evidence?: SubmitEvidenceBundle;
  };
  Element: HTMLDivElement;
}

const noop = () => {};

/** Shows evidence terms and Submit; evidence.canSubmit includes employer eligibility locks as well as checklist completion. */
export default class SubmitEvidenceActions extends Component<SubmitEvidenceActionsSignature> {
  @service declare intl: IntlService;

  get programTemplate() {
    return this.args.application.tasProgramInstance?.tasProgramTemplate;
  }

  get isTermsDone(): boolean {
    return this.args.evidence ? isStepDone(this.args.evidence.checklist, 'terms') : false;
  }

  /** Outstanding requirements as prose, empty once the checklist is satisfied. */
  get remainingSummary(): string {
    return summarizeRemaining(
      this.args.evidence?.checklist.remaining ?? [],
      this.intl.primaryLocale
    );
  }

  /**
   * Whether submitting is a thing this application can currently do at all, and
   * so whether the Submit control renders.
   *
   * The card deliberately renders in review and settled states. The API accepts
   * `request_evidence_approval` only from ATTEND (plus the DEFINE_COURSES and
   * EVIDENCE_APPROVED legs this screen does not offer), so outside it the button
   * had nothing to fire and no honest reason to give: explaining a block there
   * would tell someone their employer's rules are stopping a request they
   * already submitted. It is withheld rather than disabled — the card stays an
   * upload-and-review surface, with the terms above still legible.
   */
  get isSubmittable(): boolean {
    return this.args.application.state === 'TAS.ApplicationState.ATTEND';
  }

  /**
   * Why Submit is disabled, or an empty string when it is not.
   *
   * Two distinct causes, and conflating them is what made the old silent
   * disabled button unfixable from the employee's side: unfinished checklist
   * items they can act on, versus an employer eligibility lock they cannot.
   */
  get blockedReason(): string {
    if (!this.args.evidence || this.args.evidence.canSubmit || !this.isSubmittable) {
      return '';
    }
    if (this.remainingSummary) {
      return this.intl.t('tuition_assistance.applications.manage.tasks.checklist_submit_blocked', {
        items: this.remainingSummary,
      });
    }
    return this.intl.t(
      'tuition_assistance.applications.manage.tasks.checklist_submit_blocked_policy'
    );
  }

  /**
   * Province `application_approve_evidence_workflow` — the evidence-time terms
   * specifically, distinct from the course-approval and program-level sets the
   * same template carries for other moments in the lifecycle.
   */
  get conditionsForSubmit(): readonly ConditionModel[] {
    if (EVIDENCE_UPLOAD_ONLY_STATES.has(this.args.application.state)) {
      return [];
    }
    return this.programTemplate?.applicationApproveEvidenceConditions ?? [];
  }

  <template>
    <div class="tio-col gap-4" data-test-submit-evidence-actions ...attributes>
      {{#if this.conditionsForSubmit.length}}
        <ChecklistRow
          @title={{t "tuition_assistance.applications.manage.tasks.checklist_terms_title"}}
          @isDone={{this.isTermsDone}}
        >
          <AcceptConditions
            @conditions={{this.conditionsForSubmit}}
            @onChange={{if @evidence.onAcceptConditions @evidence.onAcceptConditions noop}}
          />
        </ChecklistRow>
      {{/if}}

      {{#if this.isSubmittable}}
        <div class="tio-row flex-wrap items-center gap-3">
          <Button
            @intent="primary"
            data-test-submit-evidence-cta
            @isDisabled={{not @evidence.canSubmit}}
            @isRunning={{@evidence.isSubmitting}}
            {{on "click" (if @evidence.onSubmit @evidence.onSubmit noop)}}
          >
            <PhUploadSimple @size={{16}} class="mr-2" />
            {{t "tuition_assistance.applications.manage.tasks.submit_evidence_cta"}}
          </Button>
          {{#if this.blockedReason}}
            <span class="tio-text-sm-normal tio-fg-secondary" data-test-submit-evidence-blocked>
              {{this.blockedReason}}
            </span>
          {{/if}}
          {{#if @evidence.submitError}}
            <ErrorMessages
              @showErrors={{true}}
              @error={{@evidence.submitError}}
              class="text-sm mt-2"
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
